/**
 * Effects feature (plan G8, order `FEATURE_ORDER.fx`): everything that turns combat into light on screen,
 * through the asset lane's `VfxMaterials` and recipes exactly as the P0 contract defines them.
 *
 * - Particle pools, one per VFX material (particles.ts), simulated in float64 world space (emitters.ts).
 * - Combat events → recipes (combatFx.ts), read after every tick so each effect is born at its event's sim
 *   time and velocity; the frame then advances every particle to the interpolated render time, so a muzzle
 *   flash sits on the muzzle a fast ship is drawn at, not a tick behind it.
 * - Projectile rounds as tracer and bolt streaks (projectileView.ts); torpedoes as `ord.torpedo` models with
 *   lit motors and smoke trails (torpedoView.ts).
 * - `bus 'quality'` → the pools are rebuilt at the preset's particle cap (spec §8: 256 / 1,024 / 4,096).
 *
 * `afterTick` and `frame` allocate nothing (D-014).
 */
import { Group } from 'three/webgpu'
import type { FrameInfo, GameContext, GameFeature } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { SIM_DT } from '../../core/loop'
import { logger } from '../../core/logger'
import { FACTION_IDS, type QualityPreset } from '../../render/contracts'
import { createVfxMaterials } from '../../render/materials'
import { createCombatFxReader, readCombatEvents, type CombatFxReader } from './combatFx'
import { loadFxConfig, poolCapacities, type FxConfig } from './config'
import {
  beginParticleFrame,
  clearParticleSystem,
  createParticleSystem,
  disposeParticleSystem,
  drawnInstances,
  endParticleFrame,
  liveParticles,
  setParticleCapacities,
  stepParticleSystem,
  type ParticleSystem,
} from './particles'
import { createProjectileView, drawProjectiles, type ProjectileView } from './projectileView'
import { createTorpedoViews, updateTorpedoViews, type TorpedoView, type TorpedoViews } from './torpedoView'

/** Seed salt so the effects RNG never shares a sequence with the sim's (effects stay out of determinism). */
const FX_SEED_SALT = 0x0f1e2d3c
/** A render clock this far behind the particles' means the sim clock was reset: drop the particles. */
const CLOCK_RESET_SECONDS = 1

/** Live counts for the perf overlay (G14). Updated every frame; never reallocated. */
export interface FxStats {
  /** Simulated particles alive. */
  particles: number
  /** Instances drawn this frame (particles + projectile streaks). */
  drawn: number
  /** Projectile streaks drawn this frame. */
  rounds: number
  /** Torpedo views on screen. */
  torpedoes: number
}

/** The feature plus read access for tests and the dev tools. */
export interface FxFeature extends GameFeature {
  readonly config: FxConfig
  readonly stats: FxStats
  readonly system: ParticleSystem | null
  readonly projectiles: ProjectileView | null
  readonly torpedoes: TorpedoViews | null
  readonly reader: CombatFxReader | null
  afterTick(ctx: GameContext): void
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

/** The sim time the frame is drawn at: `alpha` of the way through the last tick. */
export function renderTime(simTime: number, alpha: number): number {
  return simTime - (1 - alpha) * SIM_DT
}

interface FxState {
  system: ParticleSystem
  projectiles: ProjectileView
  torpedoes: TorpedoViews
  reader: CombatFxReader
  unsubscribe: () => void
}

/**
 * Compiles the pool pipelines (and a torpedo view's) at load, so the first shot never stalls on a shader
 * compile. Best effort: a failure only means the first use compiles.
 */
async function warmPipelines(ctx: GameContext, s: FxState): Promise<void> {
  const holder = new Group()
  const list = s.system.list
  for (let i = 0; i < list.length; i++) {
    list[i].mesh.visible = true
    list[i].geometry.instanceCount = 1
  }
  holder.add(s.system.root)
  let torpedo: TorpedoView | undefined
  for (const f of FACTION_IDS) torpedo ??= s.torpedoes.free[f][0]
  if (torpedo) holder.add(torpedo.instance.root)
  try {
    await ctx.renderer.compileAsync(holder, ctx.camera, ctx.scene)
  } catch (err: unknown) {
    logger.warn('fx: pipeline warm-up failed; effects compile on first use', err)
  } finally {
    for (let i = 0; i < list.length; i++) {
      list[i].mesh.visible = false
      list[i].geometry.instanceCount = 0
    }
    if (torpedo) torpedo.instance.root.removeFromParent()
    ctx.scene.add(s.system.root)
  }
}

/** Per-frame work (allocation-free, D-014). */
function frameFx(ctx: GameContext, s: FxState, config: FxConfig, stats: FxStats, alpha: number): void {
  const sim = ctx.sim
  const now = renderTime(sim.time, alpha)
  if (now < s.system.time - CLOCK_RESET_SECONDS) clearParticleSystem(s.system, now)
  // Events emitted outside a tick (dev tools) are drawn now.
  readCombatEvents(s.reader, s.system, sim.events, sim.data.weapons, sim.defs, config.explosions, now, now)
  updateTorpedoViews(s.torpedoes, sim.torpedoes, sim.data.weapons, sim.defs, s.system, alpha, ctx.rig.pos, now)
  stepParticleSystem(s.system, now)
  beginParticleFrame(s.system, ctx.rig.pos)
  drawProjectiles(s.projectiles, sim.projectiles, sim.data.weapons, alpha, ctx.rig.pos)
  endParticleFrame(s.system)
  stats.particles = liveParticles(s.system)
  stats.drawn = drawnInstances(s.system)
  stats.rounds = s.projectiles.drawn
  stats.torpedoes = s.torpedoes.live
}

export function createFxFeature(config: FxConfig = loadFxConfig(), options: { warmUp?: boolean } = {}): FxFeature {
  let state: FxState | null = null
  const stats: FxStats = { particles: 0, drawn: 0, rounds: 0, torpedoes: 0 }

  function capsFor(preset: QualityPreset): ReturnType<typeof poolCapacities> {
    return poolCapacities(config.caps[preset], config.weights, config.minPerPool)
  }

  const feature: FxFeature = {
    name: 'fx',
    order: FEATURE_ORDER.fx,
    config,
    stats,
    get system(): ParticleSystem | null {
      return state?.system ?? null
    },
    get projectiles(): ProjectileView | null {
      return state?.projectiles ?? null
    },
    get torpedoes(): TorpedoViews | null {
      return state?.torpedoes ?? null
    },
    get reader(): CombatFxReader | null {
      return state?.reader ?? null
    },
    async init(ctx: GameContext): Promise<void> {
      const sim = ctx.sim
      const vfx = createVfxMaterials(ctx.lib)
      const system = createParticleSystem(vfx, vfx.recipes, capsFor(ctx.quality), (sim.seed ^ FX_SEED_SALT) >>> 0)
      system.time = sim.time
      ctx.scene.add(system.root)

      const model = ctx.models.get(config.torpedo.model)
      if (!model) throw new Error(`fx: torpedo model ${config.torpedo.model} was not loaded`)
      const torpedoes = createTorpedoViews({
        model,
        lib: ctx.lib,
        parent: ctx.scene,
        lodScale: ctx.views.lodScale,
        engineSocket: config.torpedo.engineSocket,
        capacity: sim.torpedoes.capacity,
        recipes: vfx.recipes,
      })
      torpedoes.time = sim.time
      // Prewarm the liveries of every ship that carries torpedoes.
      const warmed = new Set<string>()
      for (const def of sim.defs) {
        if (def.torpedoRounds <= 0 || warmed.has(def.faction)) continue
        warmed.add(def.faction)
        torpedoes.prewarm(def.faction, config.torpedo.prewarm)
      }

      const s: FxState = {
        system,
        projectiles: createProjectileView(sim.data.weapons, config.tracers, system),
        torpedoes,
        reader: createCombatFxReader(sim.events),
        unsubscribe: ctx.bus.on('quality', (preset) => setParticleCapacities(system, capsFor(preset))),
      }
      state = s
      if (options.warmUp ?? true) await warmPipelines(ctx, s)
    },
    afterTick(ctx: GameContext): void {
      const s = state
      if (!s) return
      const sim = ctx.sim
      readCombatEvents(s.reader, s.system, sim.events, sim.data.weapons, sim.defs, config.explosions, sim.time - SIM_DT, sim.time)
    },
    frame(ctx: GameContext, f: FrameInfo): void {
      const s = state
      if (!s) return
      frameFx(ctx, s, config, stats, f.alpha)
    },
    dispose(): void {
      const s = state
      if (!s) return
      s.unsubscribe()
      s.torpedoes.dispose()
      disposeParticleSystem(s.system)
      state = null
    },
  }
  return feature
}

const feature: FxFeature = createFxFeature()
export default feature
